import { Button, MenuItem, Select, Typography, Chip, SelectChangeEvent } from "@mui/material";
import React, { useMemo } from "react";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import DashboardLayout from "../../../../Layouts/Dashboard/dashboard";
import { ModalContext } from "../../../../Components/Modals";
import ProvidersDetailPanel from "../../../../Components/Panels/ProvidersDetailPanel/providers-detail-panel";
import { useToggle } from "../../../../Utils/Hooks/useToggle";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp } from "@mui/x-data-grid";
import { ingestService, setupService } from "../../../../Core/Api/services";
import { ProviderAndStorage } from "../../../../Core/Api/Types/provider-and-storage";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { RejectionRating } from "../../../../Core/Api/Types/rejection-rating";
import TableCellRatingRequests from "../../../../Components/Table/TableCellRatingRequests/table-cell-rating-requests";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TableCellEnabled from "../../../../Components/Table/TableCellEnabled/table-cell-enabled";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { DbLabProviderInfos } from "../../../../Core/Api/Types/db-lab-provider-infos";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import ModalConfirmAction, {
    ModalConfirmActionElementProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import ProviderEditPanel from "../../../../Components/Panels/ProviderEdit/provider-edit";
import ModalAddProvider, {
    ModalAddProviderElementProps,
} from "../../../../Components/Modals/ModalAddProvider/modal-add-provider";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import ClearIcon from "@mui/icons-material/Clear";
import { UserHasAcls } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import TableCellTypography from "../../../../Components/Table/TableCellTypography/table-cell-typography";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../Components/Table/table-uuids";

function ProvidersIndex() {
    const { t } = useTranslation();
    const { active: providerDetailPanelOpen, toggle: toggleProvidersDetailPanelOpen } = useToggle();
    const { active: providerEditPanelOpen, toggle: toggleProviderEditPanelOpen } = useToggle();
    const [filterRank, setFilterRank] = React.useState<number>(-1);
    const [enable, setEnable] = React.useState<string[]>([]);
    const [initLoad, setInitLoad] = React.useState(0);
    const [filterEnable, setFilterEnable] = React.useState<string>("Default");
    const [filterAffinityCheck, setFilterAffinityCheck] = React.useState<string>("Default");
    const [isLoading, setIsLoading] = React.useState(true);
    const [providers, setProviders] = React.useState<any[]>([]);
    const [providerToEdit, setProviderToEdit] = React.useState<DbLabProviderInfos>();
    const [selectedProviderId, setSelectedProviderId] = React.useState<number>();
    const [searchText, setSearchText] = React.useState<string>("");
    const [selectedProvider, setSelectedProvider] = React.useState<
        ProviderAndStorage & { rank: RejectionRating } & { index: number }
    >();
    const apiGrid = useGridApiRef();

    const customSnackbar = useCustomSnackbar();

    const modalContext = React.useContext(ModalContext);
    const { openModal } = { ...modalContext };

    const userHasRightToDelete = useMemo(() => {
        return UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclBackofficeLabProviderCreate]);
    }, []);

    React.useEffect(() => {
        setEnable(Object.values(["Enabled", "Disabled"]));
    }, []);

    React.useEffect(() => {
        setIsLoading(true);

        const getProviders = setupService.GetAllProviders();
        const getRjections = ingestService.GetRejectionsRatings();

        Promise.all([getProviders, getRjections]).then(([providersResponse, ratingsResponse]) => {
            let providers = providersResponse.providersAndStorages as ProviderAndStorage[];
            let ratings = ratingsResponse.ratings as RejectionRating[];
            let finalArray = [] as any[];

            for (let i = 0; i < providers.length; i++) {
                let rating = ratings.find((itmInner) => itmInner.labProviderId === providers[i].provider?.id);

                finalArray.push({
                    ...providers[i],
                    rank: rating ?? { rate: 0, acceptedPercentage: 0, requestsCount: 0 },
                });
            }

            finalArray = finalArray.sort((a, b) => {
                if (a.rank.requestsCount > b.rank.requestsCount) {
                    return -1;
                } else if (a.rank.requestsCount < b.rank.requestsCount) {
                    return 1;
                } else {
                    if (a.rank.acceptedPercentage > b.rank.acceptedPercentage) return -1;
                    else if (a.rank.acceptedPercentage < b.rank.acceptedPercentage) return 1;
                    else return 0;
                }
            });

            finalArray = finalArray.map((arr, index) => ({ ...arr, index: index + 1 }));

            setProviders(finalArray);
            setIsLoading(false);
        });
    }, [initLoad]);

    // Table creation
    const rows: GridRowsProp = providers;
    type Row = (typeof rows)[number];
    const columns: GridColDef[] = [
        {
            field: "provider.id",
            headerName: "Id",
            sortable: false,
            minWidth: CellSize.xsmall,
            flex: CellSize.xsmall,
            valueGetter: (params: any, row: any) => {
                return row?.provider.id;
            },
            renderCell: (data: Row) => {
                return (
                    <Typography color="primary.text" variant="body1" fontWeight={500}>
                        #{data.value}
                    </Typography>
                );
            },
        },
        {
            field: "provider.labName",
            headerName: t("common:name"),
            sortable: false,
            minWidth: CellSize.xlarge,
            flex: CellSize.xlarge,
            valueGetter: (params: any, row: any) => {
                return row?.provider.labName;
            },
            renderCell: (data: Row) => {
                return <TableCellTypography data={data.value} color="text.primary" fontWeight={500} />;
            },
        },
        {
            field: "provider.labEmail",
            headerName: t("common:contactEmail"),
            sortable: false,
            minWidth: CellSize.large,
            flex: CellSize.large,
            valueGetter: (params: any, row: any) => {
                return row?.provider.labEmail;
            },
            renderCell: (data: Row) => {
                return <TableCellTypography data={data.value} color="text.secondary" />;
            },
        },
        {
            field: "index",
            headerName: t("common:rank"),
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: Row) => <Chip label={data.value} icon={<EmojiEventsIcon />} />,
        },
        {
            field: "rank.requestsCount",
            headerName: t("table:headerName.requests"),
            minWidth: CellSize.xsmall,
            flex: CellSize.xsmall,
            valueGetter: (params: any, row: any) => {
                return row?.rank.requestsCount;
            },
            renderCell: (data: Row) => <TableCellRatingRequests data={data.value} />,
        },
        {
            field: "rank.acceptedPercentage",
            headerName: `${t("common:accepted")} %`,
            sortable: false,
            minWidth: CellSize.small,
            flex: CellSize.small,
            type: "number",
            align: "left",
            headerAlign: "left",
            valueGetter: (params: any, row: any) => {
                return row?.rank.acceptedPercentage;
            },
            renderCell: (data: Row) => {
                return <Chip color="success" label={data.value ? `${data.value ?? 0}%` : "0%"} variant="outlined" />;
            },
        },
        {
            field: "rank.rejectedPercentage",
            headerName: `${t("table:headerName.rejections")} %`,
            sortable: false,
            minWidth: CellSize.small,
            flex: CellSize.small,
            type: "number",
            align: "left",
            headerAlign: "left",
            valueGetter: (params: any, row: any) => {
                return row?.rank.rejectedPercentage;
            },
            renderCell: (data: Row) => {
                return (
                    <Chip
                        color="error"
                        label={data.value ? `${data.value ?? 0}%` : "0%"}
                        icon={<ClearIcon />}
                        variant="outlined"
                    />
                );
            },
        },
        {
            field: "provider.disabled",
            headerName: t("common:enabled"),
            sortable: false,
            type: "boolean",
            minWidth: CellSize.xsmall,
            flex: CellSize.xsmall,
            valueGetter: (params: any, row: any) => {
                return !row?.provider.disabled;
            },
            renderCell: (data: Row) => {
                return <TableCellEnabled value={data.value} />;
            },
        },
        {
            field: "provider.disableAffinityCheck",
            headerName: t("common:affinityCheck"),
            sortable: false,
            minWidth: CellSize.medium,
            flex: CellSize.medium,
            type: "boolean",
            valueGetter: (params: any, row: any) => {
                return !row?.provider.disableAffinityCheck;
            },
            renderCell: (data: Row) => {
                return <TableCellEnabled value={data.value} />;
            },
        },
        {
            field: "actions",
            headerName: "",
            type: "actions",
            align: "right",
            minWidth: CellSize.actionsSmall,
            flex: CellSize.actionsSmall,
            getActions: (params: GridRowParams) => {
                if (!params || !params.row) return [];
                const actions = [
                    <GridActionsCellItem
                        icon={<EditOutlinedIcon sx={{ width: 20, height: 20 }} />}
                        label={t("common:editUpperCase")}
                        onClick={() => handleEditProvider(params?.row?.provider)}
                        showInMenu
                    />,
                ];
                if (userHasRightToDelete)
                    actions.push(
                        <GridActionsCellItem
                            icon={<DeleteOutlineIcon sx={{ width: 20, height: 20 }} />}
                            label={t("common:delete")}
                            color="error"
                            onClick={() => handleDeleteProvider(params?.row?.provider.id as number)}
                            showInMenu
                        />
                    );
                actions.push(
                    <GridActionsCellItem
                        icon={<MoreHorizIcon sx={{ width: 20, height: 20 }} />}
                        label={t("table:cellLabel.moreInfos")}
                        onClick={() => openPanelForDetailsMenuCall(params.row)}
                        showInMenu
                    />
                );

                return actions;
            },
        },
    ];

    const handleNewProviderClick = React.useCallback(() => {
        if (!openModal) {
            return;
        }

        const onClose: ModalAddProviderElementProps["onClose"] = (data) => {
            // Reload table data if success
            if (data.confirm) setInitLoad((prev) => ++prev);
        };

        openModal<ModalAddProviderElementProps>(ModalAddProvider, { onClose });
    }, [openModal]);

    // Delete provider
    const handleDeleteProvider = (providerId: number) => {
        if (!openModal) return;

        openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
            title: t("modal:title.deleteThisProvider"),
            commentLabel: t("modal:comment.confirmDeleteProvider"),
            onConfirm: () => HandleDeleteProviderConfirm(providerId),
        });
    };

    const HandleDeleteProviderConfirm = (id: number) => {
        setupService.DeleteProvider(id).then((response) => {
            if (response.success) customSnackbar(t("alert:success.providerDeleted"), "success");
            else customSnackbar(response.message || t("alert:error.undefinedError"), "error");
        });
    };

    const handleEditProvider = (provider) => {
        setProviderToEdit(provider);
        if (providerDetailPanelOpen) {
            setSelectedProvider(undefined);
            toggleProvidersDetailPanelOpen();
        }
        if (!providerEditPanelOpen) toggleProviderEditPanelOpen();
    };

    const openPanelForDetails = (e: GridRowParams) => {
        setSelectedProvider(e.row);
        if (providerEditPanelOpen) {
            setProviderToEdit(undefined);
            toggleProviderEditPanelOpen();
        }
        if (!providerDetailPanelOpen) toggleProvidersDetailPanelOpen();
    };

    const openPanelForDetailsMenuCall = (row) => {
        setSelectedProvider(row);
        if (providerEditPanelOpen) {
            setProviderToEdit(undefined);
            toggleProviderEditPanelOpen();
        }
        toggleProvidersDetailPanelOpen();
    };

    const handleFilterEnable = (event: SelectChangeEvent<string>) => {
        setFilterEnable(event.target.value as string);
    };

    const additionalFilters = (
        <>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={filterEnable}
                onChange={(value) => handleFilterEnable(value)}
            >
                <MenuItem value={"Default"}>{t("common:enability")}</MenuItem>
                {enable.map((status, index) => (
                    <MenuItem value={status} key={index}>
                        {status}
                    </MenuItem>
                ))}
            </Select>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={filterAffinityCheck}
                onChange={(e) => setFilterAffinityCheck(e.target.value)}
            >
                <MenuItem value={"Default"}>{t("common:affinityCheck")}</MenuItem>
                {enable.map((status, index) => (
                    <MenuItem value={status} key={index}>
                        {status}
                    </MenuItem>
                ))}
            </Select>
        </>
    );

    return (
        <DashboardLayout noScroll sxContent={{ px: 2 }}>
            <DataTable
                loading={isLoading}
                tableKey={TableUuids.PROVIDERS_TABLE}
                rows={providers.filter(
                    (x) =>
                        (searchText === "" || x.provider.labName?.includesAllWords(searchText)) &&
                        (filterRank === -1 || x.index === filterRank) &&
                        (filterEnable === "Default" ||
                            (x.provider.disabled === false && filterEnable === "Enabled") ||
                            (x.provider.disabled === true && filterEnable === "Disabled")) &&
                        (filterAffinityCheck === "Default" ||
                            (x.provider.disableAffinityCheck === false && filterAffinityCheck === "Enabled") ||
                            (x.provider.disableAffinityCheck === true && filterAffinityCheck === "Disabled"))
                )}
                columns={columns}
                initialState={{ pinnedColumns: { left: ["provider.id", "provider.labName"], right: ["actions"] } }}
                additionalActions={
                    <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleNewProviderClick}>
                        {t("common:newProvider")}
                    </Button>
                }
                additionalFilters={additionalFilters}
                disableRowSelectionOnClick
                getRowId={(row: any) => row.provider.id}
                onRowClick={openPanelForDetails}
                apiRef={apiGrid}
            />

            {selectedProvider && (
                <ProvidersDetailPanel
                    provider={selectedProvider!}
                    open={providerDetailPanelOpen}
                    onClose={toggleProvidersDetailPanelOpen}
                />
            )}
            {providerToEdit && (
                <ProviderEditPanel
                    provider={providerToEdit!}
                    open={providerEditPanelOpen}
                    onClose={toggleProviderEditPanelOpen}
                />
            )}
        </DashboardLayout>
    );
}

export default ProvidersIndex;
